import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '..';
import Icons from '../Icons/Icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { richText } from '../../util/richText';
import { SUBSCRIPTION_TYPES } from '../../util/enums';

import css from './AgencyListingCard.module.css';
import { getUserSubscription } from '../../util/userDataExtraction';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const AgencyListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    isLandingListingCard,
    isSearchPage,
  } = props;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const classes = classNames(rootClassName || css.root, className, css.brandListingCard, {
    [css.landingListingCard]: isLandingListingCard,
  });

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description, publicData } = currentListing.attributes || {};

  const { pub_primary_industry, pub_job_title, city = '', state = '' } = publicData || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'scaled-medium',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const navigateToListing = () => {
    history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {}));
  };

  const agencyPlusMaybe = Boolean(
    getUserSubscription(listing.author) === SUBSCRIPTION_TYPES.AGENCY_PLUS
  );

  return (
    <>
      <div className={classes} onClick={navigateToListing}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>

        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.cardTitle}>
              <h2>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </h2>
              <span className={css.brandPremier}>
                <Icons name="verify" /> <FormattedMessage id="BrandListingCard.verified" />
                {agencyPlusMaybe
                  ? SUBSCRIPTION_TYPES.AGENCY_PLUS
                  : SUBSCRIPTION_TYPES.AGENCY_STANDARD}
              </span>
            </div>
            {isSearchPage && (
              <>
                {pub_primary_industry && (
                  <div>
                    <p>
                      <strong>
                        <FormattedMessage id="BrandListingCard.industry" />
                      </strong>{' '}
                      {pub_primary_industry}
                    </p>
                  </div>
                )}
                {/* {pub_job_title && (
                  <div>
                    <p>
                      <strong>
                        <FormattedMessage id="BrandListingCard.jobTitle" />
                      </strong>{' '}
                      {pub_job_title}
                    </p>
                  </div>
                )} */}
              </>
            )}

            <p className={css.cardDescription}>{description}</p>
            <p className={css.cardDescription}>{`${city}, ${state}`}</p>
          </div>

          <NamedLink className={css.findLink} name="LandingPage">
            <FormattedMessage id="BrandListingCard.explore" />
            <Icons name="rightArrow" />{' '}
          </NamedLink>
        </div>
      </div>
    </>
  );
};

AgencyListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

AgencyListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(AgencyListingCardComponent);
